if (!customElements.get('amazon-product-viewer')) {
  customElements.define('amazon-product-viewer', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.section = this.closest('[id^="MainProduct-"]');
      this.data = JSON.parse(this.querySelector('[data-amazon-media]').textContent);
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.am-stage');
      this.image = this.querySelector('.am-image');
      this.thumbs = this.querySelector('.am-thumbnails');
      this.counter = this.querySelector('.am-counter');
      this.videoStage = this.querySelector('.am-video-stage');
      this.videoPlayer = this.querySelector('.am-video-player');
      this.mode = 'images';
      this.pointers = new Map();
      this.scale = 1;
      this.pan = { x: 0, y: 0 };
      this.panel = document.createElement('div');
      this.panel.className = 'am-hover-panel';
      this.panel.hidden = true;
      this.panel.setAttribute('aria-hidden', 'true');
      this.panelImage = document.createElement('img');
      this.panelImage.alt = '';
      this.panel.append(this.panelImage);
      this.lens = document.createElement('div');
      this.lens.className = 'am-lens';
      this.lens.hidden = true;
      this.lens.setAttribute('aria-hidden', 'true');
      document.body.append(this.panel, this.lens);
      this.desktop = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      this.listen(this.section, 'pointerover', e => this.onGalleryOver(e));
      this.listen(this.section, 'pointermove', e => this.onHover(e));
      this.listen(this.section, 'pointerout', e => {
        if (e.target.closest('.product__modal-opener--image') && !e.relatedTarget?.closest('.product__modal-opener--image')) this.hideHover();
      });
      this.listen(this.section, 'click', e => this.onGalleryClick(e), true);
      this.listen(this.dialog, 'click', e => this.onDialogClick(e));
      this.listen(this.dialog, 'keydown', e => {
        const tab = e.target.closest('[data-am-tab]');
        if (tab && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
          const tabs = Array.from(this.querySelectorAll('[data-am-tab]'));
          const next = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (tabs.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          this.setMode(tabs[next].dataset.amTab);
          tabs[next].focus();
          return;
        }
        if (e.target.closest('video, iframe, input')) return;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const step = e.key === 'ArrowRight' ? 1 : -1;
          if (this.mode === 'videos') this.selectVideo(this.videoIndex + step);
          else this.select(this.index + step);
        }
      });
      this.listen(this.dialog, 'close', () => this.onClose());
      this.listen(this.stage, 'wheel', e => {
        e.preventDefault();
        this.setScale(this.scale + (e.deltaY < 0 ? .25 : -.25));
      }, { passive: false });
      this.listen(this.stage, 'dblclick', e => {
        if (!e.target.closest('button')) this.setScale(this.scale > 1 ? 1 : 2.5);
      });
      this.listen(this.stage, 'pointerdown', e => this.pointerDown(e));
      this.listen(this.stage, 'pointermove', e => this.pointerMove(e));
      this.listen(this.stage, 'pointerup', e => this.pointerUp(e, true));
      this.listen(this.stage, 'pointercancel', e => this.pointerUp(e, false));
      this.listen(window, 'scroll', () => this.hideHover(), { passive: true, capture: true });
      this.listen(window, 'resize', () => { this.hideHover(); this.applyTransform(); });
      this.observer = new MutationObserver(() => this.enhanceGalleries());
      this.observer.observe(this.section, { childList: true, subtree: true });
      this.enhanceGalleries();
    }

    listen(target, event, handler, options = {}) {
      if (typeof options === 'boolean') options = { capture: options };
      target.addEventListener(event, handler, { ...options, signal: this.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      clearTimeout(this.hoverTimer);
      if (this.dialog?.open) this.dialog.close();
      this.stopVideo();
      this.panel?.remove();
      this.lens?.remove();
      if (this.oldOverflow !== undefined) document.body.style.overflow = this.oldOverflow;
      this.controller = null;
    }

    enhanceGalleries() {
      this.section.querySelectorAll('media-gallery').forEach(gallery => {
        gallery.classList.add('am-gallery');
        if (!gallery.querySelector('.am-hint')) {
          const hint = document.createElement('p');
          hint.className = 'am-hint';
          hint.textContent = this.data.labels.hint;
          gallery.querySelector('[id^="GalleryViewer"]').after(hint);
        }
        const slider = gallery.querySelector('.thumbnail-slider');
        const videoThumb = this.querySelector('[data-am-video-thumb]');
        if (slider && this.data.videos.length && videoThumb && !slider.querySelector('[data-am-video]')) {
          slider.append(videoThumb.content.cloneNode(true));
          slider.resetPages?.();
        }
      });
    }

    mediaFor(element) {
      const item = element.closest('[data-media-id]');
      if (!item) return null;
      const id = item.dataset.mediaId.split('-').pop();
      return this.data.images.find(image => image.id === id);
    }

    onGalleryOver(e) {
      if (!this.desktop.matches) return;
      const item = e.target.closest('.thumbnail-list__item[data-target]');
      if (!item || item.contains(e.relatedTarget)) return;
      const gallery = item.closest('media-gallery');
      const id = item.dataset.target.split('-').pop();
      if (!this.data.images.some(image => image.id === id)) return;
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => {
        if (!item.matches(':hover') || typeof gallery.setActiveMedia !== 'function') return;
        this.hideHover();
        gallery.setActiveMedia(item.dataset.target, false);
        item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, 100);
    }

    onGalleryClick(e) {
      const videoButton = e.target.closest('[data-am-video]');
      const videoOpener = e.target.closest('.product__media-item')?.querySelector('video, iframe, deferred-media');
      const videoMedia = videoOpener && this.data.videos.find(video => video.id === e.target.closest('[data-media-id]')?.dataset.mediaId.split('-').pop());
      if (videoButton || (videoMedia && e.target.closest('.deferred-media__poster, .product__media-toggle'))) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const target = videoButton || e.target.closest('button');
        this.openDialog(target.closest('media-gallery'), target, 'videos', videoMedia?.id);
        return;
      }
      const opener = e.target.closest('.product__modal-opener--image');
      if (!opener || !this.dialog.showModal) return;
      const media = this.mediaFor(opener);
      if (!media) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.openDialog(opener.closest('media-gallery'), opener.querySelector('button') || opener, 'images', media.id);
    }

    openDialog(gallery, returnFocus, mode, mediaId) {
      clearTimeout(this.hoverTimer);
      this.hideHover();
      window.pauseAllMedia?.();
      this.section.querySelectorAll('video').forEach(video => video.pause());
      this.gallery = gallery;
      const visibleIds = Array.from(this.gallery.querySelectorAll('.thumbnail-list__item[data-target]'))
        .filter(item => getComputedStyle(item).display !== 'none')
        .map(item => item.dataset.target.split('-').pop());
      this.images = visibleIds.length ? visibleIds.map(id => this.data.images.find(image => image.id === id)).filter(Boolean) : this.data.images;
      if (mode === 'images' && !this.images.some(image => image.id === mediaId)) this.images = this.data.images;
      this.index = Math.max(0, this.images.findIndex(image => image.id === mediaId));
      this.videoIndex = Math.max(0, this.data.videos.findIndex(video => video.id === mediaId));
      this.returnFocus = returnFocus;
      this.oldOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.setMode(mode);
      (this.mode === 'videos' ? this.videoStage : this.stage).focus();
    }

    setMode(mode) {
      this.stopVideo();
      this.mode = mode === 'videos' && this.data.videos.length ? 'videos' : 'images';
      const videoMode = this.mode === 'videos';
      this.querySelectorAll('[data-am-tab]').forEach(tab => {
        const selected = tab.dataset.amTab === this.mode;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.stage.hidden = videoMode;
      this.videoStage.hidden = !videoMode;
      this.querySelector('.am-body').classList.toggle('is-video-mode', videoMode);
      this.querySelector('.am-body').classList.toggle('has-video-list', videoMode && this.data.videos.length > 1);
      this.querySelector('.am-tools').hidden = videoMode;
      this.querySelector('.am-help').hidden = videoMode;
      this.buildThumbnails();
      if (videoMode) this.selectVideo(this.videoIndex || 0);
      else this.select(this.index || 0);
    }

    stopVideo() {
      this.videoPlayer?.querySelectorAll('video').forEach(video => video.pause());
      this.videoPlayer?.replaceChildren();
    }

    selectVideo(index) {
      if (!this.data.videos.length) return;
      this.stopVideo();
      this.videoIndex = (index + this.data.videos.length) % this.data.videos.length;
      const media = this.data.videos[this.videoIndex];
      const template = Array.from(this.querySelectorAll('[data-am-video-id]')).find(template => template.dataset.amVideoId === media.id);
      if (template) this.videoPlayer.append(template.content.cloneNode(true));
      this.querySelector('.am-video-title').textContent = media.title;
      this.counter.textContent = `${this.videoIndex + 1} / ${this.data.videos.length}`;
      this.thumbs.querySelectorAll('button').forEach((button, i) => button.setAttribute('aria-current', String(i === this.videoIndex)));
      this.thumbs.children[this.videoIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.videoPlayer.querySelector('video')?.play()?.catch(() => {});
    }

    buildThumbnails() {
      this.thumbs.replaceChildren();
      const videos = this.mode === 'videos';
      (videos ? this.data.videos : this.images).forEach((media, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'am-thumb';
        button.dataset[videos ? 'amVideoIndex' : 'amIndex'] = index;
        button.setAttribute('aria-label', `${videos ? this.data.labels.video : this.data.labels.image} ${index + 1}`);
        const image = document.createElement('img');
        image.src = media.thumbnail;
        image.alt = videos ? media.title : media.alt;
        image.loading = 'lazy';
        button.append(image);
        this.thumbs.append(button);
      });
    }

    select(index) {
      if (!this.images.length) return;
      this.index = (index + this.images.length) % this.images.length;
      const media = this.images[this.index];
      this.image.src = media.url;
      this.image.alt = media.alt;
      this.scale = 1;
      this.pan = { x: 0, y: 0 };
      this.pointers.clear();
      this.applyTransform();
      this.counter.textContent = `${this.index + 1} / ${this.images.length}`;
      this.thumbs.querySelectorAll('button').forEach((button, i) => button.setAttribute('aria-current', String(i === this.index)));
      this.thumbs.children[this.index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      this.querySelector('[data-am-prev]').hidden = this.images.length < 2;
      this.querySelector('[data-am-next]').hidden = this.images.length < 2;
    }

    onDialogClick(e) {
      const button = e.target.closest('button');
      if (button?.hasAttribute('data-am-close')) this.dialog.close();
      else if (button?.dataset.amTab) this.setMode(button.dataset.amTab);
      else if (button?.dataset.amVideoIndex !== undefined) this.selectVideo(Number(button.dataset.amVideoIndex));
      else if (button?.hasAttribute('data-am-prev')) this.select(this.index - 1);
      else if (button?.hasAttribute('data-am-next')) this.select(this.index + 1);
      else if (button?.hasAttribute('data-am-in')) this.setScale(this.scale + .5);
      else if (button?.hasAttribute('data-am-out')) this.setScale(this.scale - .5);
      else if (button?.hasAttribute('data-am-reset')) this.setScale(1);
      else if (button?.dataset.amIndex !== undefined) this.select(Number(button.dataset.amIndex));
      else if (e.target === this.dialog) {
        const r = this.dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.dialog.close();
      }
    }

    onClose() {
      this.stopVideo();
      document.body.style.overflow = this.oldOverflow || '';
      this.oldOverflow = undefined;
      this.pointers.clear();
      const selected = this.images?.[this.index];
      const item = this.mode === 'images' && selected ? Array.from(this.gallery.querySelectorAll('[data-media-id]')).find(item => item.dataset.mediaId.endsWith(`-${selected.id}`)) : null;
      if (item && typeof this.gallery.setActiveMedia === 'function') this.gallery.setActiveMedia(item.dataset.mediaId, false);
      const focusTarget = item?.querySelector('.product__modal-opener--image button') || this.returnFocus;
      focusTarget?.focus({ preventScroll: true });
    }

    setScale(scale) {
      this.scale = Math.max(1, Math.min(4, scale));
      if (this.scale === 1) this.pan = { x: 0, y: 0 };
      this.applyTransform();
    }

    applyTransform() {
      const r = this.stage.getBoundingClientRect();
      const maxX = r.width * (this.scale - 1) / 2;
      const maxY = r.height * (this.scale - 1) / 2;
      this.pan.x = Math.max(-maxX, Math.min(maxX, this.pan.x));
      this.pan.y = Math.max(-maxY, Math.min(maxY, this.pan.y));
      this.image.style.transform = `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.scale})`;
      this.stage.classList.toggle('is-zoomed', this.scale > 1);
      this.querySelector('[data-am-out]').disabled = this.scale <= 1;
      this.querySelector('[data-am-in]').disabled = this.scale >= 4;
    }

    pointerDown(e) {
      if (e.target.closest('button') || (e.pointerType === 'mouse' && e.button !== 0)) return;
      this.stage.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.dragStart = { x: e.clientX, y: e.clientY, panX: this.pan.x, panY: this.pan.y };
      this.gestureHadPinch = this.pointers.size > 1;
      if (this.pointers.size === 2) {
        this.pinchStart = this.distance();
        this.pinchScale = this.scale;
      }
      this.stage.classList.add('is-dragging');
    }

    distance() {
      const [a, b] = Array.from(this.pointers.values());
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    pointerMove(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 2 && this.pinchStart > 0) this.setScale(this.pinchScale * this.distance() / this.pinchStart);
      else if (this.pointers.size === 1 && this.scale > 1) {
        this.pan.x = this.dragStart.panX + e.clientX - this.dragStart.x;
        this.pan.y = this.dragStart.panY + e.clientY - this.dragStart.y;
        this.applyTransform();
      }
    }

    pointerUp(e, allowSwipe) {
      if (!this.pointers.has(e.pointerId)) return;
      if (allowSwipe && this.pointers.size === 1 && !this.gestureHadPinch && this.scale === 1 && e.pointerType !== 'mouse') {
        const deltaX = e.clientX - this.dragStart.x;
        if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(e.clientY - this.dragStart.y)) this.select(this.index + (deltaX < 0 ? 1 : -1));
      }
      this.pointers.delete(e.pointerId);
      if (this.pointers.size === 1) {
        const p = Array.from(this.pointers.values())[0];
        this.dragStart = { ...p, panX: this.pan.x, panY: this.pan.y };
      }
      if (!this.pointers.size) this.stage.classList.remove('is-dragging');
    }

    hideHover() {
      this.panel.hidden = true;
      this.lens.hidden = true;
    }

    onHover(e) {
      const opener = e.target.closest('.product__modal-opener--image');
      if (!this.desktop.matches || !opener || this.dialog.open || e.pointerType === 'touch') return this.hideHover();
      const media = this.mediaFor(opener);
      const image = opener.querySelector('img');
      if (!media || !image?.complete || !image.naturalWidth) return this.hideHover();
      const box = image.getBoundingClientRect();
      const fit = getComputedStyle(image).objectFit;
      let width = box.width, height = box.height, left = box.left, top = box.top;
      if (fit === 'contain') {
        const ratio = Math.min(box.width / image.naturalWidth, box.height / image.naturalHeight);
        width = image.naturalWidth * ratio;
        height = image.naturalHeight * ratio;
        left += (box.width - width) / 2;
        top += (box.height - height) / 2;
      }
      if (e.clientX < left || e.clientX > left + width || e.clientY < top || e.clientY > top + height) return this.hideHover();
      const panelLeft = box.right + 20;
      const panelWidth = innerWidth - panelLeft - 20;
      if (panelWidth < 220) return this.hideHover();
      const sticky = document.querySelector('.shopify-section-header');
      const headerBottom = sticky ? sticky.getBoundingClientRect().bottom : 0;
      const bottomReserve = document.getElementById('OnlineStorePreviewBarNextData') ? 70 : 20;
      const panelTop = Math.max(16, Math.min(innerHeight - bottomReserve - 240, Math.max(box.top, headerBottom + 12)));
      const panelHeight = innerHeight - panelTop - bottomReserve;
      if (panelHeight < 160) return this.hideHover();
      const zoom = 2.8;
      const lensWidth = Math.min(width, panelWidth / zoom);
      const lensHeight = Math.min(height, panelHeight / zoom);
      const x = Math.max(lensWidth / 2, Math.min(width - lensWidth / 2, e.clientX - left));
      const y = Math.max(lensHeight / 2, Math.min(height - lensHeight / 2, e.clientY - top));
      if (this.panelImage.getAttribute('src') !== media.url) this.panelImage.src = media.url;
      Object.assign(this.panel.style, { left: `${panelLeft}px`, top: `${panelTop}px`, width: `${panelWidth}px`, height: `${panelHeight}px` });
      Object.assign(this.panelImage.style, { width: `${width * zoom}px`, height: `${height * zoom}px`, left: `${panelWidth / 2 - x * zoom}px`, top: `${panelHeight / 2 - y * zoom}px` });
      Object.assign(this.lens.style, { left: `${left + x - lensWidth / 2}px`, top: `${top + y - lensHeight / 2}px`, width: `${lensWidth}px`, height: `${lensHeight}px` });
      this.panel.hidden = false;
      this.lens.hidden = false;
    }
  });
}
